import { useCallback, useEffect, useRef } from "react";
import {
  CMD_ID_DIRECTION,
  CMD_ID_NECK,
  CMD_ID_STOP,
  DIRECTION_DOWN,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_STOP,
  DIRECTION_UP,
  NECK_DOWN,
  NECK_LEFT,
  NECK_RIGHT,
  NECK_STOP,
  NECK_UP,
  NECK_YAW_RESET,
} from "@/common/cmd";
import { track } from "@/common/utils";
import { ICallAdapter } from "../adapters/CallAdapter";

interface KeyboardControlState {
  callAdapter?: ICallAdapter;
  setCallAdapter: (adapter: ICallAdapter) => void;
}

const useKeyboardControl = (): KeyboardControlState => {
  const callAdapterRef = useRef<ICallAdapter>();
  const yawResetTimeoutRef = useRef<any>(null);

  const setCallAdapter = useCallback((adapter: ICallAdapter) => {
    callAdapterRef.current = adapter;
  }, []);

  const resetNeckYaw = () => {
    clearTimeout(yawResetTimeoutRef.current);
    // 3秒后reset
    yawResetTimeoutRef.current = setTimeout(() => {
      callAdapterRef.current?.sendMessage(CMD_ID_NECK, NECK_YAW_RESET);
    }, 3000);
  };

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      e.preventDefault();

      // 防止重复触发
      if (e.repeat) return;

      if (!callAdapterRef.current) return;

      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          await callAdapterRef.current.sendMessage(
            CMD_ID_DIRECTION,
            DIRECTION_UP
          );
          track("keyboard", { action: "up" });
          break;
        case "ArrowDown":
        case "s":
        case "S":
          await callAdapterRef.current.sendMessage(
            CMD_ID_DIRECTION,
            DIRECTION_DOWN
          );
          track("keyboard", { action: "down" });
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          await callAdapterRef.current.sendMessage(
            CMD_ID_DIRECTION,
            DIRECTION_LEFT
          );
          track("keyboard", { action: "left" });
          break;
        case "ArrowRight":
        case "d":
        case "D":
          await callAdapterRef.current.sendMessage(
            CMD_ID_DIRECTION,
            DIRECTION_RIGHT
          );
          track("keyboard", { action: "right" });
          break;
        case "j":
        case "J":
          await callAdapterRef.current.sendMessage(CMD_ID_NECK, NECK_LEFT);
          track("keyboard", { action: "neck_left" });
          break;
        case "l":
        case "L":
          await callAdapterRef.current.sendMessage(CMD_ID_NECK, NECK_RIGHT);

          track("keyboard", { action: "neck_right" });
          break;
        case "i":
        case "I":
          await callAdapterRef.current.sendMessage(CMD_ID_NECK, NECK_UP);
          track("keyboard", { action: "neck_up" });
          break;
        case "k":
        case "K":
          await callAdapterRef.current.sendMessage(CMD_ID_NECK, NECK_DOWN);
          track("keyboard", { action: "neck_down" });
          break;
        case " ":
          await callAdapterRef.current.sendMessage(CMD_ID_STOP, DIRECTION_STOP);
          track("keyboard", { action: "stop" });
          break;
        default:
          break;
      }
    };

    const handleKeyUp = async (e: KeyboardEvent) => {
      e.preventDefault();

      if (!callAdapterRef.current) return;

      switch (e.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
        case "w":
        case "W":
        case "s":
        case "S":
        case "a":
        case "A":
        case "d":
        case "D":
          await callAdapterRef.current.sendMessage(CMD_ID_STOP, DIRECTION_STOP);
          track("keyboard", { action: "direction_stop" });
          break;
        case "j":
        case "J":
        case "l":
        case "L":
        case "i":
        case "I":
        case "k":
        case "K":
          await callAdapterRef.current.sendMessage(CMD_ID_NECK, NECK_STOP);
          track("keyboard", { action: "neck_stop" });
          switch (e.key) {
            case "j":
            case "J":
            case "l":
            case "L":
              resetNeckYaw();
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return {
    callAdapter: callAdapterRef.current,
    setCallAdapter,
  };
};

export default useKeyboardControl;
